// src/components/Preview/PreviewComponent.js
import React, { useEffect, useRef, useState } from "react";

const environment_name = process.env.REACT_APP_STAGE_ENV?.toLowerCase();

const PREVIEW_ROOT_ID = "sageion-preview-root";

const SOCKET_IO_URL = "https://cdn.socket.io/4.1.2/socket.io.min.js";
const getBundleUrl = () =>
  environment_name === "prod"
    ? "https://magicchat-core.github.io/prod-ssc-client-cdns/bundle.js"
    : "https://magicchat-core.github.io/dev-ssc-client-cdns/bundle.js";

const cryptoPolyfill = `
  (function() {
    if (typeof window !== "undefined") {
      if (!window.crypto) { window.crypto = {}; }
      if (!window.crypto.randomUUID) {
        window.crypto.randomUUID = function randomUUID() {
          var d = new Date().getTime();
          if (typeof performance !== "undefined" && typeof performance.now === "function") {
            d += performance.now();
          }
          return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            var r = ((d + Math.random() * 16) % 16) | 0;
            d = Math.floor(d / 16);
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });
        };
      }
      if (!window.crypto.getRandomValues) {
        window.crypto.getRandomValues = function getRandomValues(arr) {
          for (var i = 0; i < arr.length; i++) { arr[i] = Math.floor(Math.random() * 256); }
          return arr;
        };
      }
      if (!window.crypto.subtle) { window.crypto.subtle = {}; }
    }
  })();
`;

export default function PreviewComponent({ appData }) {
  const containerRef = useRef(null);
  const socketScriptRef = useRef(null);
  const bundleScriptRef = useRef(null);

  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [magicchatStatus, setMagicchatStatus] = useState("Loading...");

  const [currentUserId, setCurrentUserId] = useState(null);

  // Bumping this remounts the #preview-body via React's key mechanism
  const [refreshTick, setRefreshTick] = useState(0);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginInput, setLoginInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // -----------------------------------------------------------------
  // SCOPED CLEANUP
  //   Removes scripts, Sageion globals, and any leftover widget nodes.
  //   It does NOT manually remove React-managed nodes — React handles that
  //   when the key on <div id="preview-body"> changes.
  // -----------------------------------------------------------------
  const cleanupMagicChat = () => {
    console.log("DEBUG PREVIEW:: Cleanup (scoped)...");
    try {
      // 1. Remove our own script tags
      [socketScriptRef, bundleScriptRef].forEach((ref) => {
        if (ref.current && ref.current.parentNode) {
          ref.current.parentNode.removeChild(ref.current);
          ref.current = null;
        }
      });

      // 2. Remove any floating widget Sageion appended at document root
      //    (React doesn't own these — Sageion appended them directly to body)
      const floatingSelectors = [
        '[id^="sageion-root"]',
        '[id^="sageion-widget"]',
        '[id^="magicchat-root"]',
        '[id^="magicchat-widget"]',
        ".sageion-root",
        ".sageion-widget",
        ".magicchat-root",
        ".magicchat-widget",
      ];
      floatingSelectors.forEach((sel) => {
        try {
          document.querySelectorAll(sel).forEach((el) => {
            if (el.parentNode) el.parentNode.removeChild(el);
          });
        } catch (_) { }
      });

      // 3. Global references Sageion sets
      if (window.sageion_os) {
        try { delete window.sageion_os; } catch (_) { window.sageion_os = undefined; }
      }
      if (window.__REST_API_KEY__) {
        try { delete window.__REST_API_KEY__; } catch (_) { window.__REST_API_KEY__ = undefined; }
      }

      // 4. Clear any other Sageion/MagicChat globals that might cache widget state
      const suspiciousGlobals = Object.keys(window).filter((k) => {
        const lower = k.toLowerCase();
        return (
          lower.includes("sageion") ||
          lower.includes("magicchat") ||
          lower.includes("tezkit") ||
          k.startsWith("__SAGEION") ||
          k.startsWith("__MAGICCHAT")
        );
      });
      suspiciousGlobals.forEach((k) => {
        if (k === "__SAGEION_SETUP_DONE__") return;
        try { delete window[k]; } catch (_) { window[k] = undefined; }
        console.log("DEBUG PREVIEW:: Cleared global:", k);
      });

      // 5. Clear Sageion's localStorage keys
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (
            k &&
            (k.startsWith("tezkit_") ||
              k.toLowerCase().includes("sageion") ||
              k.toLowerCase().includes("magicchat"))
          ) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        if (keysToRemove.length) {
          console.log("DEBUG PREVIEW:: Cleared localStorage keys:", keysToRemove);
        }
      } catch (_) { }

      // 6. Reset the setup flag so setUp() runs again after refresh
      window.__SAGEION_SETUP_DONE__ = false;

      setIsScriptLoaded(false);
      setIsInitialized(false);
      setMagicchatStatus("Cleaned up");
    } catch (err) {
      console.warn("DEBUG PREVIEW:: Cleanup error:", err);
    }
  };

  // -----------------------------------------------------------------
  // Crypto polyfill (once)
  // -----------------------------------------------------------------
  useEffect(() => {
    try {
      new Function(cryptoPolyfill)();
    } catch (err) {
      console.warn("DEBUG PREVIEW:: Crypto polyfill failed:", err);
    }
  }, []);

  // -----------------------------------------------------------------
  // Load scripts (runs on mount and every refreshTick change)
  // -----------------------------------------------------------------
  useEffect(() => {
    if (isScriptLoaded || !appData) return;

    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) return resolve(existing);
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => resolve(s);
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
      });

    (async () => {
      try {
        console.log("DEBUG PREVIEW:: Loading scripts (tick:", refreshTick, ")");
        const socketScript = await loadScript(SOCKET_IO_URL);
        socketScriptRef.current = socketScript;

        const bundleScript = await loadScript(getBundleUrl());
        bundleScriptRef.current = bundleScript;

        console.log("DEBUG PREVIEW:: Scripts loaded");
        setIsScriptLoaded(true);
      } catch (err) {
        console.error("DEBUG PREVIEW:: Script load error:", err);
        setError(err.message);
        setMagicchatStatus("Error: Bundle load failed");
      }
    })();

    
    return () => {
      console.log("DEBUG PREVIEW:: Script effect cleanup");
      cleanupMagicChat();
    };

  }, [appData, refreshTick]);

  // -----------------------------------------------------------------
  // setUp() — once per "page load"
  // -----------------------------------------------------------------
  const ensureSetup = async () => {
    if (!window.sageion_os?.setUp) {
      throw new Error("sageion_os.setUp not available");
    }
    if (window.__SAGEION_SETUP_DONE__) return;

    const version = appData.settings?.version || "V2";

    console.log("DEBUG PREVIEW:: Running setUp() (fresh page load)");
    await window.sageion_os.setUp(
      appData.app_id || "",
      appData.auth_key || "",
      appData.region || "US",
      "preview-body"
    );

    if (version === "V2" && appData.rest_api_key) {
      window.__REST_API_KEY__ = appData.rest_api_key;
    }

    window.__SAGEION_SETUP_DONE__ = true;
  };

  // -----------------------------------------------------------------
  // initialize() — reflects current identity
  // -----------------------------------------------------------------
  const initializeMagicChat = async (userId = null) => {
    if (!window.sageion_os?.initialize) {
      setError("sageion_os.initialize not available");
      setMagicchatStatus("Error: Not available");
      return;
    }

    const container = document.getElementById("preview-body");
    if (!container) {
      setError("Container element not found");
      setMagicchatStatus("Error: Container missing");
      return;
    }

    try {
      setIsLoading(true);
      setMagicchatStatus("Initializing...");

      await ensureSetup();

      const uid = userId || null;
      const payload = uid ? { uid } : {};

      await window.sageion_os.initialize(payload);

      console.log("DEBUG PREVIEW:: initialize() done with", payload);

      setCurrentUserId(uid);
      setIsInitialized(true);
      setShowLoginModal(false);

      setMagicchatStatus(
        uid ? `Connected ✓ (User: ${uid})` : "Connected ✓ (Anonymous)"
      );

      if (window.parent) {
        window.parent.postMessage(
          {
            type: "preview-ready",
            message: "Sageion initialized successfully",
            userId: uid,
          },
          "*"
        );
      }
    } catch (err) {
      console.error("DEBUG PREVIEW:: initialize error:", err);
      setError(err.message);
      setMagicchatStatus(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // -----------------------------------------------------------------
  // Kick off initialize once scripts are ready
  // -----------------------------------------------------------------
  useEffect(() => {
    if (isScriptLoaded && appData && !isInitialized) {
      initializeMagicChat(currentUserId);
    }

  }, [appData, isScriptLoaded, isInitialized, refreshTick]);

  // -----------------------------------------------------------------
  // Refresh — bump the key. React unmounts the old #preview-body
  // (and everything inside it, including the open chat widget),
  // then remounts a fresh one. The scripts effect re-runs, setUp()
  // runs again, initialize() restores the identity. All clean.
  // -----------------------------------------------------------------
  const handleRefresh = () => {
    console.log("DEBUG PREVIEW:: ⟳ Hard refresh via key change");

    // Preserve identity across refresh
    const identityToKeep = currentUserId;

    // 1. Tear down scripts + globals + floating widget (Sageion-owned stuff)
    cleanupMagicChat();

    // 2. Reset UI states — the key change will remount the body fresh
    setError(null);
    setIsInitialized(false);
    setMagicchatStatus("Loading...");

    // 3. Restore identity so next initialize reflects it
    if (identityToKeep) {
      setCurrentUserId(identityToKeep);
    } else {
      setCurrentUserId(null);
    }

    // 4. Re-apply crypto polyfill (idempotent)
    try {
      new Function(cryptoPolyfill)();
    } catch (_) { }

    // 5. Bump the tick — this remounts #preview-body and re-runs effects
    setRefreshTick((t) => t + 1);
  };

  // -----------------------------------------------------------------
  // User actions
  // -----------------------------------------------------------------
  const handleLogin = () => {
    const uid = loginInput.trim();
    if (!uid) {
      alert("Please enter a user ID");
      return;
    }
    setShowLoginModal(false);
    initializeMagicChat(uid);
  };

  const handleLogout = async () => {
    try {
      if (window.sageion_os?.logout) {
        await window.sageion_os.logout();
      }
    } catch (err) {
      console.warn("DEBUG PREVIEW:: logout() failed:", err);
    }
    setCurrentUserId(null);
    setLoginInput("");
    initializeMagicChat(null);
  };

  const toggleLogin = () => {
    if (currentUserId) {
      handleLogout();
    } else {
      setShowLoginModal(true);
    }
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  if (!appData) {
    return (
      <div className="preview-empty">
        <p>No app data available for preview</p>
      </div>
    );
  }

  const hasIdentity = !!currentUserId;

  return (
    <div className="preview-component" ref={containerRef} id={PREVIEW_ROOT_ID}>
      <div className="preview-header">
        <h4>Live Preview: {appData.app_name}</h4>
        <div className="preview-info">
          <span
            className={`status-indicator ${magicchatStatus.includes("Connected") ? "status-success" : ""
              }`}
          >
            {magicchatStatus}
          </span>
          <button
            onClick={toggleLogin}
            className={`login-toggle-btn ${hasIdentity ? "logout" : "login"}`}
            title={hasIdentity ? "Clear identity" : "Identify as a user"}
          >
            {hasIdentity ? "🔓 Clear Identity" : "🔑 Identify User"}
          </button>
          <button
            onClick={handleRefresh}
            className="refresh-btn"
            title="Refresh and reinitialize"
          >
            ⟳
          </button>
        </div>
      </div>

      {/* 🔑 key={refreshTick} forces React to unmount + remount this node */}
      <div id="preview-body" key={refreshTick} className="preview-body-inner">
        {error ? (
          <div className="preview-error">
            <p>Error: {error}</p>
            <button onClick={handleRefresh}>Retry</button>
          </div>
        ) : !isScriptLoaded ? (
          <div className="preview-loading">
            <div className="spinner"></div>
            <p>Loading Sageion...</p>
          </div>
        ) : isLoading ? (
          <div className="preview-loading">
            <div className="spinner"></div>
            <p>Initializing Sageion...</p>
          </div>
        ) : (
          <div className="magicchat-container">
            <div className="preview-info-text">
              <p>✅ Sageion widget will appear in the bottom right corner</p>
              <p>✅ Click the widget to open the chat interface</p>
              <p>✅ Send test messages to verify connectivity</p>
              <p className={`user-info ${hasIdentity ? "" : "user-info-anon"}`}>
                👤{" "}
                {hasIdentity
                  ? `Identified as: ${currentUserId}`
                  : "Anonymous session"}
              </p>
            </div>
            <div className="preview-details">
              <p><strong>App Name:</strong> {appData.app_name}</p>
              <p><strong>Version:</strong> {appData.settings?.version || "V1"}</p>
              <p><strong>Region:</strong> {appData.region || "US"}</p>
              <p><strong>Type:</strong> {appData.settings?.app_type || "P2A"}</p>
              <p>
                <strong>Status:</strong>{" "}
                {hasIdentity
                  ? `🟢 Identified (${currentUserId})`
                  : "⚪ Anonymous Session"}
              </p>
            </div>
          </div>
        )}
      </div>

      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Identify User</h3>
            <p
              style={{
                textAlign: "center",
                color: "#666",
                marginBottom: "20px",
              }}
            >
              Enter a previously onboarded user ID to switch the preview session
            </p>
            <div className="form-group">
              <label htmlFor="userId">User ID</label>
              <input
                type="text"
                id="userId"
                value={loginInput}
                onChange={(e) => setLoginInput(e.target.value)}
                placeholder="Enter user ID"
                autoComplete="off"
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleLogin();
                }}
              />
            </div>
            <div className="form-group">
              <button
                onClick={handleLogin}
                className="btn-primary"
                disabled={isLoading}
              >
                {isLoading ? "Initializing..." : "Identify"}
              </button>
            </div>
            <button
              className="btn-close"
              onClick={() => setShowLoginModal(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="preview-footer">
        <small>
          App: {appData.app_name} ({appData.app_id}) | Region: {appData.region} |
          Type: {appData.settings?.app_type}
        </small>
      </div>

      <style>{`
        .preview-component {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          gap: 12px;
          flex-wrap: nowrap;
        }
        .preview-header h4 {
          margin: 0;
          color: #333;
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex-shrink: 1;
          min-width: 0;
        }
        .preview-info {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
          flex-wrap: nowrap;
        }
        .status-indicator {
          padding: 5px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          background: #fff3cd;
          color: #856404;
          white-space: nowrap;
          line-height: 1.2;
        }
        .status-indicator.status-success {
          background: #d4edda;
          color: #155724;
        }
        .login-toggle-btn {
          padding: 5px 12px;
          border-radius: 6px;
          border: 1px solid #dee2e6;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          line-height: 1.2;
          background: white;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .login-toggle-btn.login {
          color: #007bff;
          border-color: #007bff;
        }
        .login-toggle-btn.login:hover {
          background: #007bff;
          color: white;
        }
        .login-toggle-btn.logout {
          color: #dc3545;
          border-color: #dc3545;
        }
        .login-toggle-btn.logout:hover {
          background: #dc3545;
          color: white;
        }
        .refresh-btn {
          background: none;
          border: 1px solid #dee2e6;
          font-size: 14px;
          cursor: pointer;
          padding: 5px 9px;
          border-radius: 6px;
          transition: all 0.2s;
          line-height: 1.2;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .refresh-btn:hover {
          background: #e9ecef;
          border-color: #adb5bd;
        }
        #preview-body {
          flex: 1;
          padding: 24px;
          min-height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fafafa;
          overflow-y: auto;
        }
        .preview-loading {
          text-align: center;
        }
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .preview-error {
          text-align: center;
          color: #721c24;
          background: #f8d7da;
          padding: 20px;
          border-radius: 8px;
        }
        .preview-error button {
          margin-top: 12px;
          padding: 8px 16px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        .magicchat-container {
          width: 100%;
          text-align: center;
        }
        .preview-info-text {
          color: #666;
          margin: 10px 0;
          font-size: 13px;
        }
        .preview-info-text .user-info {
          color: #28a745;
          font-weight: 500;
          margin-top: 15px;
          padding: 8px 12px;
          background: #d4edda;
          border-radius: 4px;
          display: inline-block;
        }
        .preview-info-text .user-info-anon {
          color: #6c757d;
          background: #e9ecef;
        }
        .preview-details {
          margin-top: 20px;
          padding: 16px 20px;
          background: #f8f9fa;
          border-radius: 8px;
          text-align: left;
          border-left: 4px solid #007bff;
        }
        .preview-details p {
          margin: 6px 0;
          color: #333;
          font-size: 13px;
        }
        .preview-details strong {
          color: #666;
          font-weight: 600;
          display: inline-block;
          min-width: 80px;
        }
        .preview-footer {
          padding: 10px 16px;
          background: #f8f9fa;
          border-top: 1px solid #e9ecef;
          color: #6c757d;
          font-size: 11px;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .preview-empty {
          padding: 40px;
          text-align: center;
          color: #6c757d;
        }
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .modal-content {
          background: white;
          padding: 30px;
          border-radius: 12px;
          width: 90%;
          max-width: 400px;
          position: relative;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          animation: slideUp 0.3s ease;
        }
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .modal-content h3 {
          margin: 0 0 10px 0;
          color: #333;
          text-align: center;
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #555;
          font-weight: 500;
        }
        .form-group input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 16px;
          box-sizing: border-box;
        }
        .form-group input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }
        .btn-primary {
          width: 100%;
          padding: 14px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
        }
        .btn-primary:hover {
          background: #0056b3;
        }
        .btn-primary:disabled {
          background: #ccc;
          cursor: not-allowed;
        }
        .btn-close {
          position: absolute;
          top: 10px;
          right: 15px;
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #999;
          transition: color 0.2s;
        }
        .btn-close:hover {
          color: #333;
        }
        @media (max-width: 420px) {
          .preview-header {
            padding: 10px 12px;
            gap: 8px;
          }
          .preview-header h4 {
            font-size: 13px;
          }
          .status-indicator {
            font-size: 10px;
            padding: 4px 8px;
          }
          .login-toggle-btn {
            font-size: 10px;
            padding: 4px 8px;
          }
          .refresh-btn {
            padding: 4px 7px;
            font-size: 13px;
          }
          .preview-footer {
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
}